import { z } from "zod";

/**
 * Shared contract for the user-selectable placeholder (initials) avatar
 * colors.
 *
 * Historically the initials-avatar background/text color was derived from a
 * char-code hash of whatever `name` string the caller happened to pass. That
 * is deterministic per *string*, but the Edit Profile dialog seeds the preview
 * from the live, editable name field — so the color visibly flickered on every
 * keystroke, and different panes that passed a different name variant
 * (full name vs. display name vs. email) showed the SAME user in DIFFERENT
 * colors. Users read that as "the color keeps changing".
 *
 * The fix has two halves that both live here so the FE and BE agree:
 *   1. A STABLE deterministic default keyed on an identity string (user id /
 *      email) instead of the editable name — `getDeterministicAvatarColors`.
 *   2. Optional persisted overrides (`avatarBackgroundColor` /
 *      `avatarTextColor`) the user can pick in the dialog, validated as 6-digit
 *      hex on both ends.
 */

// Six legible background/text pairings lifted from the dali palette
// (carbon, vermillion, cadmium, egyptian-blue, malachite, ochre). Order is
// preserved from the previous class-based hash so an existing user keyed on
// the same string keeps the same default color family.
export const AVATAR_COLOR_PRESETS: readonly {
  name: string;
  backgroundColor: string;
  textColor: string;
}[] = [
  { name: "carbon", backgroundColor: "#d5d9d9", textColor: "#272f3b" },
  { name: "vermillion", backgroundColor: "#ffb9a3", textColor: "#7e1100" },
  { name: "cadmium", backgroundColor: "#fed8de", textColor: "#881330" },
  { name: "egyptian-blue", backgroundColor: "#dbeafe", textColor: "#19368a" },
  { name: "malachite", backgroundColor: "#d6eeb1", textColor: "#064e3e" },
  { name: "ochre", backgroundColor: "#fee899", textColor: "#78340d" },
];

/**
 * Mongoose `.populate(path, select)` projection for any user reference that
 * feeds an initials avatar (comment authors, participants, reviewers,
 * creatives, mentions, etc.). Historically these projected only
 * `"_id name avatar"`, which silently stripped the user's persisted avatar
 * colors so OTHER people always saw the deterministic default instead of the
 * colors that user picked. Keep every avatar-bearing populate in sync via this
 * constant so the color fields can never be dropped again.
 */
export const AVATAR_USER_POPULATE_FIELDS =
  "_id name avatar avatarBackgroundColor avatarTextColor";

/** Matches a 3- or 6-digit hex color, e.g. `#fff` or `#ffb9a3`. */
export const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export function isValidHexColor(value: string): boolean {
  return HEX_COLOR_REGEX.test(value);
}

/** Zod schema for an optional persisted avatar color (6/3-digit hex). */
export const avatarHexColorSchema = z
  .string()
  .regex(HEX_COLOR_REGEX, "Must be a hex color like #ffb9a3");

/**
 * Deterministically map a stable identity key (user id, email, or as a last
 * resort the name) to one of the preset color pairs. Same input ALWAYS yields
 * the same pair — this is what stops the placeholder color from changing
 * between renders / panes / keystrokes.
 */
export function getDeterministicAvatarColors(key: string | undefined | null): {
  backgroundColor: string;
  textColor: string;
} {
  const str = key ?? "";
  let hash = 0;
  for (let i = 0; i < Math.min(str.length, 100); i++) {
    hash += str.charCodeAt(i);
  }
  const index = Math.abs(hash) % AVATAR_COLOR_PRESETS.length;
  const preset = AVATAR_COLOR_PRESETS[index];
  return {
    backgroundColor: preset.backgroundColor,
    textColor: preset.textColor,
  };
}
